import type { CognitoRefusalCode, ExchangeRefusalCode, HostRefusalCode } from './types';

export interface DeniedCopy {
  title: string;
  description: string;
}

export interface DeniedCopyParams {
  source: string | null;
  code: string | null;
}

// Record<RefusalCode, DeniedCopy> is deliberate: adding a refusal code to types.ts
// fails compilation here until user-facing copy exists for it.
const EXCHANGE_COPY: Record<ExchangeRefusalCode, DeniedCopy> = {
  unauthorized: {
    title: 'We could not verify your sign-in',
    description: 'Your identity could not be confirmed. Try signing in again.',
  },
  account_access_denied: {
    title: 'Access to this account was denied',
    description: 'Your account is not authorized to access this workspace. Contact your administrator.',
  },
  account_not_provisioned: {
    title: 'This account is still being set up',
    description: 'Your account is not fully provisioned yet. Check back soon.',
  },
  no_workspace_assigned: {
    title: 'You have not been assigned a workspace',
    description:
      'Your account administrator needs to give you access to a workspace before you can sign in. Contact them to get set up.',
  },
};

const COGNITO_COPY: Record<CognitoRefusalCode, DeniedCopy> = {
  access_denied: {
    title: 'Sign-in was cancelled',
    description: 'The sign-in request was cancelled.',
  },
  invalid_request: {
    title: 'Sign-in request was invalid',
    description: 'The sign-in request could not be completed. Try signing in again.',
  },
  unauthorized_client: {
    title: 'Sign-in is not permitted',
    description: 'This application is not permitted to sign you in.',
  },
  invalid_scope: {
    title: 'Sign-in permissions could not be granted',
    description: 'The requested sign-in permissions were rejected.',
  },
  unsupported_response_type: {
    title: 'Sign-in is not supported',
    description: 'The sign-in method used is not supported.',
  },
};

const HOST_COPY: Record<HostRefusalCode, DeniedCopy> = {
  identity_refused: {
    title: 'You are signed in, but this account cannot use this application',
    description: 'Contact your administrator, or sign out and use a different account.',
  },
};

const GENERIC_COPY: DeniedCopy = {
  title: 'Unable to sign in',
  description: 'Something went wrong while signing you in. Contact your administrator if this continues.',
};

// hasOwnProperty (not `in`/direct index) so prototype-chain names ("toString") coming
// off the URL never resolve to copy.
function findCopy<Code extends string>(table: Record<Code, DeniedCopy>, code: string): DeniedCopy | undefined {
  return Object.prototype.hasOwnProperty.call(table, code) ? table[code as Code] : undefined;
}

export function lookupDeniedCopy({ source, code }: DeniedCopyParams): DeniedCopy {
  if (source === 'exchange' && code) {
    return findCopy(EXCHANGE_COPY, code) ?? GENERIC_COPY;
  }
  if (source === 'cognito' && code) {
    return findCopy(COGNITO_COPY, code) ?? GENERIC_COPY;
  }
  if (source === 'host' && code) {
    return findCopy(HOST_COPY, code) ?? GENERIC_COPY;
  }
  return GENERIC_COPY;
}
